import { DownloadIcon, FileIcon } from "lucide-react";
import type { EnvironmentId } from "@cmmd-forge/contracts";
import type { ChatFileAttachment } from "../../types";
import { useAttachmentDownload } from "./useAttachmentDownload";

function formatAttachmentSize(sizeBytes: number): string {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${Math.ceil(sizeBytes / 1024)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileAttachmentLink({
  environmentId,
  file,
}: {
  readonly environmentId: EnvironmentId;
  readonly file: ChatFileAttachment;
}) {
  const { download, error, pending } = useAttachmentDownload({
    environmentId,
    attachmentId: file.id,
    filename: file.name,
  });
  return (
    <a
      href={file.downloadUrl}
      download={file.name}
      aria-label={error ?? `Download ${file.name}`}
      onClick={(event) => {
        event.preventDefault();
        download();
      }}
      className="flex min-h-11 items-center gap-2 rounded-lg border border-border/80 bg-background/70 px-2 text-sm hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <FileIcon className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate">
        {error ?? (pending ? "Downloading..." : file.name)}
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {formatAttachmentSize(file.sizeBytes)}
      </span>
      <DownloadIcon className="size-4 shrink-0" aria-hidden="true" />
    </a>
  );
}
